<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-toolbar class="md-success">
        <div class="md-toolbar-row">
          <div class="md-toolbar-section-start">
            <hierarchy-menu :selectableGroup="false"></hierarchy-menu>
          </div>

          <md-button class="md-button md-just-icon md-simple" @click="openModal">
            <i style="color: #ffffff !important;" class="fas fa-cog"></i>
          </md-button>
        </div>
      </md-toolbar>
      <br />
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>power</md-icon>
          </div>
          <p class="category">{{$lang.charts.voltage}}</p>
          <h3 class="title">
            <animated-number
              :value="averageVoltage"
              :formatValue="voltageFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.U.MIN"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.U.AVG"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.U.MAX"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon">
            <i class="fas fa-bolt"></i>
          </div>
          <p class="category">{{$lang.charts.current}}</p>
          <h3 class="title">
            <animated-number
              :value="averageCurrent"
              :formatValue="currentFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.I.MIN"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.I.AVG"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.I.MAX"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>wb_incandescent</md-icon>
          </div>
          <p class="category">{{$lang.charts.active_power}}</p>
          <h3 class="title">
            <animated-number
              :value="activePower"
              :formatValue="activePowerFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.P.MIN"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.P.AVG"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.P.MAX"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="warning">
        <template slot="header">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <p class="category">{{$lang.charts.active_energy}} {{localizedDateRange()}}</p>
          <h3 class="title">
            <animated-number
              :value="energyImport"
              :formatValue="energyImportFormat"
              :duration="1000"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            {{$lang.charts.total_energy}}
            <animated-number
              v-if="energyOffset == 0"
              :value="statisticData.E_I.offsetted"
              :formatValue="energyImportFormat"
              :duration="1000"
            />

            <a
              v-else
              href="#"
              :title="$lang.general.merito_energy + ': ' + (statisticData.E_I.total / 1000).toFixed(2) + ' kWh'"
            >
              <i>
                <animated-number
                  :value="statisticData.E_I.offsetted"
                  :formatValue="energyImportFormat"
                  :duration="1000"
                />
              </i>
            </a>
            <md-button
              v-if="selectedDevice.owner && !selectedDevice.macList"
              style="margin: 0px 0px; height: 22px;"
              class="md-button md-just-icon md-simple"
              @click="modalEnergyOpen"
            >
              <md-icon>update</md-icon>
            </md-button>
          </div>
        </template>
      </stats-card>
    </div>

    <div
      v-for="graph in graphDraw"
      v-bind:key="graph.id"
      class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
    >
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <h4 class="title">{{graph.title}}</h4>
        </md-card-header>
        <md-card-content>
          <highcharts
            v-if="graph.type == 1"
            class="dashboardChart"
            :options="activeEnergyChartOptions"
            :ref="graph.ref"
          ></highcharts>
          <highcharts
            v-if="graph.type == 2"
            class="dashboardChart"
            :options="reactiveEnergyChartOptions"
            :ref="graph.ref"
          ></highcharts>
        </md-card-content>
      </md-card>
    </div>

    <div class="md-layout-item md-size-80">
      <global-sales-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>language</md-icon>
          </div>
          <h4 class="title">{{$lang.charts.locations}}</h4>
        </template>

        <template slot="content">
          <devices-stats-panel :settings="devicesStatsData"></devices-stats-panel>
          <!-- <div id="map"></div> -->
        </template>
      </global-sales-card>
    </div>

    <div class="md-layout-item md-size-20">
      <global-sales-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>share</md-icon>
          </div>
          <h4 class="title">{{$lang.charts.share}}</h4>
        </template>

        <template slot="content">
         <qr-code :text="qrcode"></qr-code>
        </template>
      </global-sales-card>
    </div>

    <modal v-if="showModal">
      <template slot="header">
        <h4 class="modal-title">{{$lang.titles.dashboard_settings}}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="closeModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <h4 class="card-title">{{$lang.titles.statistics_report}}</h4>
        <md-radio v-model="selectedDateRange" value="today">{{$lang.keys.today_stats}}</md-radio>
        <md-radio v-model="selectedDateRange" value="week">{{$lang.keys.week}}</md-radio>
        <md-radio v-model="selectedDateRange" value="month">{{$lang.keys.month}}</md-radio>
        <md-radio v-model="selectedDateRange" value="year">{{$lang.keys.year}}</md-radio>

        <h4 class="card-title">{{$lang.titles.graphs_types}}</h4>
        <p>{{$lang.titles.active_energy_report}}</p>
        <md-checkbox class="md-primary" v-model="graphTypes" value="3">{{$lang.keys.monthly}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="2">{{$lang.keys.daily}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="1">{{$lang.keys.hourly}}</md-checkbox>

        <p>{{$lang.titles.reactive_energy_report}}</p>
        <md-checkbox class="md-primary" v-model="graphTypes" value="6">{{$lang.keys.monthly}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="5">{{$lang.keys.daily}}</md-checkbox>
        <md-checkbox class="md-primary" v-model="graphTypes" value="4">{{$lang.keys.hourly}}</md-checkbox>
      </template>

      <template slot="footer">
        <md-button class="md-primary" @click="saveModalData">{{$lang.keys.save}}</md-button>
        <md-button class="md-simple" @click="closeModal">{{$lang.keys.close}}</md-button>
      </template>
    </modal>

    <modal v-if="showModalEnergy">
      <template slot="header">
        <h4 class="modal-title">{{$lang.titles.energy_edit}}</h4>
        <md-button
          class="md-simple md-just-icon md-round modal-default-button"
          @click="modalEnergyClose"
        >
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <div class="md-layout" @keyup.enter="modalEnergySave()">
          <div class="md-layout-item md-size-50">
            <md-field :class="[{ 'md-error': errors.has('E_OFFSET') }]">
              <label>{{$lang.inputs.energy_offset}}</label>
              <md-input
                v-model="energyOffsetInputVal"
                data-vv-name="E_OFFSET"
                type="text"
                name="E_OFFSET"
                v-validate="modelValidations.energy_offset"
              ></md-input>
              <slide-y-down-transition>
                <md-icon class="error" v-show="errors.has('E_OFFSET')">close</md-icon>
              </slide-y-down-transition>
              <span class="md-suffix">kWh</span>
            </md-field>
          </div>

          <div class="md-layout-item md-size-50">
            <md-field :class="[{ 'md-error': errors.has('CT_RATIO') }]">
              <label>{{$lang.inputs.transformation_ratio}}</label>
              <md-input
                v-model="CTRatioInputVal"
                data-vv-name="CT_RATIO"
                type="text"
                name="CT_RATIO"
                v-validate="modelValidations.CT_RATIO"
              ></md-input>
              <slide-y-down-transition>
                <md-icon class="error" v-show="errors.has('CT_RATIO')">close</md-icon>
              </slide-y-down-transition>
              <span class="md-suffix">:1</span>
            </md-field>
          </div>
        </div>
        <br />
      </template>

      <template slot="footer">
        <md-button class="md-primary" @click="modalEnergySave">{{$lang.keys.save}}</md-button>
        <md-button class="md-simple" @click="modalEnergyClose">{{$lang.keys.close}}</md-button>
      </template>
    </modal>

    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>

<script>
import { SlideYDownTransition } from "vue2-transitions";
import MarkerClusterer from "@google/markerclusterer";
import gmapsInit from "@/utils/gmaps";
import { Modal } from "@/components";
import snackBar from "@/components/SnackBar";
import hierarchyMenu from "@/components/HierarchyMenu";
import AnimatedNumber from "animated-number-vue";

import chartOptions from "@/config/charts/column";
import axios from "axios";
import { getSelectedDeviceToken, getUserToken } from "@/storage/memoryStorage";

import format from "date-fns/format";
import subHours from "date-fns/sub_hours";
import subDays from "date-fns/sub_days";
import start_of_today from "date-fns/start_of_today";
import end_of_today from "date-fns/end_of_today";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import startOfMonth from "date-fns/start_of_month";
import endOfMonth from "date-fns/end_of_month";
import startOfYear from "date-fns/start_of_year";
import endOfYear from "date-fns/end_of_year";
import endOfTomorrow from "date-fns/end_of_tomorrow";
import startOfHour from "date-fns/start_of_hour";

import { StatsCard, GlobalSalesCard } from "@/components";
import { startOfDay, subMonths } from "date-fns";
import { types } from "util";

import DevicesStatsPanel from "@/components/Dashboard/DevicesStatsPanel";
import CryptoJS from 'crypto-js';

export default {
  components: {
    "hierarchy-menu": hierarchyMenu,
    "snack-bar": snackBar,
    Modal,
    AnimatedNumber,
    StatsCard,
    GlobalSalesCard,
    SlideYDownTransition,
    DevicesStatsPanel
  },

  data() {
    return {
      modelValidations: {
        CT_RATIO: {
          required: true,
          regex: /^[0-9]+.{0,1}[0-9]{0,6}$/,
          max_value: 100000,
          min_value: 0.00000000001
        },
        energy_offset: {
          required: true,
          regex: /^[0-9]+.{0,1}[0-9]{0,4}$/,
          max_value: 99999999,
          min_value: 0
        }
      },
      animationDuration: 500,
      showModal: false,
      showModalEnergy: false,
      energyOffset: 0,
      energyOffsetInputVal: 0,
      CTRatioInputVal: 1,

      selectedDateRange: "",
      graphTypes: [],
      graphDraw: [],

      averageVoltage: "",
      averageCurrent: "",
      activePower: "",
      energyImport: "",

      activeEnergyChartOptions: null,
      reactiveEnergyChartOptions: null,

      statisticData: {
        loading: false,
        U: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        I: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        P: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        E_I: {
          start: 0,
          total: 0,
          offsetted: 0
        }
      },

      locations: [],
      devicesStatsData: [],
      qrcode: ""
    };
  },

  computed: {
    WSmessage() {
      return this.$store.getters.getDataMessage;
    },
    selectedDate() {
      return this.$store.getters.getDateRange;
    },
    selectedDevice() {
      return this.$store.getters.getSelectedDevice;
    },
    deviceList() {
      return this.$store.getters.getDeviceList;
    },
    statsDate() {
      return this.$store.getters.getSettings.statsDate;
    },
    selectedGraphs() {
      return this.$store.getters.getSettings.graphs;
    },
    overviewStatsUpdate() {
      return this.$store.getters.getOverviewStats;
    },
    selectedyAxisType() {
      return this.$store.getters.getSettings.yAxisType;
    },
    phasesAPI() {
      let phases = [];
      for (let i = 1; i <= this.selectedDevice.phase_use; i++) {
        phases.push("L" + i);
      }
      return phases.join(",");
    }
  },

  watch: {
    WSmessage: function(payload, oldPayload) {
      this.averageVoltage = 0;
      if (payload.U) {
        let dividerAVG = 0;
        Object.keys(payload.U).forEach(phase => {
          const value = payload.U[phase];
          if (value > 0) {
            this.averageVoltage += value;
            dividerAVG++;
          }
        });
        this.averageVoltage = this.averageVoltage / dividerAVG;
      }

      this.averageCurrent = 0;
      if (payload.I) {
        Object.keys(payload.I).forEach(phase => {
          const value = payload.I[phase];
          this.averageCurrent += value;
        });
      }

      this.activePower = 0;
      if (payload.P) {
        Object.keys(payload.P).forEach(phase => {
          const value = payload.P[phase];
          this.activePower += value;
        });
      }

      this.statisticData.E_I.total = 0;
      if (payload.E_I) {
        Object.keys(payload.E_I).forEach(phase => {
          const value = payload.E_I[phase];
          this.statisticData.E_I.total += value;
        });

        this.energyImport =
          Math.abs(this.statisticData.E_I.total - this.statisticData.E_I.start);
      } else {
        this.energyImport = 0;
      }

      if (!isNaN(this.energyOffset)) {
        this.statisticData.E_I.offsetted =
          this.statisticData.E_I.total + this.energyOffset * 1000;
      }
    },
    selectedDevice: function(newDevice, oldDevice) {
        //if (newDevice.mac !== oldDevice.mac || newDevice.macList) {
          this.getQRString();
          this.getStatisticsData();
          this.getEnergyData();
          this.getDataToCharts();
          this.getEnergyOffset();
          
        //}
    },
    overviewStatsUpdate: function(newVal, oldVal) {
      if (newVal) {
        this.getHierarchyData();
        this.$store.commit("setOverviewStats", false);
      }
    }

    /*     statsDate: function(newDevice, oldDevice) {
      if (newDevice.from !== oldDevice.from || newDevice.to !== oldDevice.to) {
        this.getStatisticsData();
        this.getEnergyData();
      }
    } */
  },

  created() {
    this.getQRString();
    this.getHierarchyData();
    this.initChartReactiveEnergy();
    this.initChartActiveEnergy();
    this.getEnergyOffset();
    
    this.selectedDateRange = this.statsDate.selector;
    this.graphTypes = this.selectedGraphs;

   
  },

  mounted() {
    
    let compStatsDateRange = this.getTSFromDateRange(this.selectedDateRange);

    if (
      compStatsDateRange.from !== this.statsDate.from ||
      compStatsDateRange.to !== this.statsDate.to
    ) {
      compStatsDateRange.selector = this.selectedDateRange;
      this.$store.commit("setstatsDate", compStatsDateRange);
    }

    this.getEnergyData();
    this.getStatisticsData();
    this.setChartsData(this.graphTypes);
    

  },

  methods: {
    localizedDateRange() {
      switch (this.selectedDateRange) {
        case "today":
          return this.$lang.keys.today_stats;
        case "week":
          return this.$lang.keys.week;
        case "month":
          return this.$lang.keys.month;
        case "year":
          return this.$lang.keys.year;
      }
    },

    getTSFromDateRange(dateRange) {
      let statDate = {
        selector: dateRange,
        from: 0,
        to: 0
      };
      switch (dateRange) {
        case "today":
          statDate.from = format(start_of_today(), "X");
          statDate.to = format(end_of_today(), "X");
          break;
        case "week":
          statDate.from = format(
            startOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          statDate.to = format(
            endOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          break;
        case "month":
          statDate.from = format(startOfMonth(start_of_today()), "X");
          statDate.to = format(endOfMonth(start_of_today()), "X");
          break;
        case "year":
          statDate.from = format(startOfYear(start_of_today()), "X");
          statDate.to = format(endOfYear(start_of_today()), "X");
          break;
      }
      return statDate;
    },

    saveModalData: function() {
      let compStatsDateRange = this.getTSFromDateRange(this.selectedDateRange);

      if (
        this.statsDate.from !== compStatsDateRange.from ||
        this.statsDate.to !== compStatsDateRange.to
      ) {
        compStatsDateRange.selector = this.selectedDateRange;
        this.$store.commit("setstatsDate", compStatsDateRange);
        this.getStatisticsData();
        this.getEnergyData();
      }

      this.$store.commit("setSelectedGraphs", this.graphTypes);
      this.setChartsData(this.graphTypes);

      this.closeModal();
    },

    setChartsData(graphsArray) {
      this.graphDraw = [];
      graphsArray.forEach(gSelect => {
        const obj = {
          id: gSelect,
          type: Number(gSelect) <= 3 ? 1 : 2,
          ref: "graph_" + gSelect,
          title: "graph_" + gSelect
        };

        switch (gSelect) {
          case "1":
            obj.title = this.$lang.charts.active_energy_last_24_hours;
            break;
          case "2":
            obj.title = this.$lang.charts.active_energy_last_14_days;
            break;
          case "3":
            obj.title = this.$lang.charts.active_energy_last_12_months;
            break;
          case "4":
            obj.title = this.$lang.charts.reactive_energy_last_24_hours;
            break;
          case "5":
            obj.title = this.$lang.charts.reactive_energy_last_14_days;
            break;
          case "6":
            obj.title = this.$lang.charts.reactive_energy_last_12_months;
            break;
        }

        this.graphDraw.push(obj);
      });
      this.getDataToCharts();
    },

    getDataToCharts() {
      
      this.$nextTick(() => {
        this.graphDraw.forEach(el => {
          let granularity = "";
          let from = 0;
          let to = 0;
          let dt;

          switch (el.id) {
            case "1":
            case "4":
              granularity = "HOUR";
              dt = new Date();
              dt.setHours(dt.getHours() + 1);
              to = format(startOfHour(dt), "X");
              from = format(subHours(startOfHour(dt), 24), "X");
              break;
            case "2":
            case "5":
              granularity = "DAY";
              dt = new Date();
              dt.setDate(dt.getDate() + 1);
              to = format(startOfDay(dt), "X");
              from = format(subDays(startOfDay(dt), 14), "X");
              break;
            case "3":
            case "6":
              granularity = "MONTH";
              dt = new Date();
              dt.setMonth(dt.getMonth() + 1);
              to = format(startOfMonth(dt), "X");
              from = format(subMonths(startOfMonth(dt), 12), "X");
              break;
          }

          this.getEnergyChartData(
            from,
            to,
            this.$refs[el.ref][0],
            granularity,
            el.type
          );
        });
      });
    },

    getEnergyOffset() {
      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/devices/details",
        data: {
          token: getSelectedDeviceToken(this.selectedDevice.mac)
        },
        withCredentials: true
      })
        .then(response => {
          this.CTRatioInputVal = response.data.data.CT_ratio;
          this.energyOffset = response.data.data.E_I_offset;
          this.energyOffsetInputVal = this.energyOffset;
          this.statisticData.E_I.offsetted =
            this.statisticData.E_I.total + this.energyOffset * 1000;
        })
        .catch(error => {
          this.energyOffset = 0;
          this.energyOffsetInputVal = this.energyOffset;
          this.statisticData.E_I.offsetted =
            this.statisticData.E_I.total + this.energyOffset * 1000;
          this.$refs.snackbar.notify(
            this.$lang.messages.api_communication_error
          );
        });
    },

    modalEnergySave() {
      this.$validator
        .validate("E_OFFSET", this.energyOffsetInputVal)
        .then(validatorResultOffset => {
          this.$validator
            .validate("CT_RATIO", this.CTRatioInputVal)
            .then(validatorResultCTRatio => {
              if (validatorResultOffset && validatorResultCTRatio) {
                this.energyOffset = this.energyOffsetInputVal;
                this.statisticData.E_I.offsetted =
                  this.statisticData.E_I.total + this.energyOffset * 1000;

                axios({
                  method: "POST",
                  url: this.$ENDPOINTS.API_W + "/devices/details",
                  data: {
                    token: getSelectedDeviceToken(this.selectedDevice.mac),
                    E_I_offset: Number(this.energyOffset),
                    CT_ratio: Number(this.CTRatioInputVal)
                  },
                  withCredentials: true
                })
                  .then(response => {
                    this.modalEnergyClose();
                  })
                  .catch(error => {
                    this.$refs.snackbar.notify(
                      this.$lang.messages.api_communication_error
                    );
                  });
              }
            });
        });
    },

    modalEnergyOpen() {
      this.showModalEnergy = true;
    },

    modalEnergyClose() {
      this.energyOffsetInputVal = this.energyOffset;
      this.showModalEnergy = false;
    },

    openModal() {
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    getStatisticsData() {
      this.statisticData.loading = true;

      let axiosConfig = {
        method: "POST",
        data: {
          data: "U,I,P",
          statData: "MIN,MAX,AVG",
          phase: "CUM",
          from: this.statsDate.from,
          to: this.statsDate.to
        },
        withCredentials: true
      };

      if (this.selectedDevice.macList) {
        axiosConfig.url = this.$ENDPOINTS.API_R + "/statistics/group";
        axiosConfig.data.user_token = getUserToken();
        axiosConfig.data.macList = this.selectedDevice.macList;
      } else {
        axiosConfig.url = this.$ENDPOINTS.API_R + "/statistics";
        axiosConfig.data.token = getSelectedDeviceToken(
          this.selectedDevice.mac
        );
      }

      
      axios(axiosConfig)
        .then(response => {
          if (response.data.status === "OK") {
            this.statisticData.U.MIN = response.data.data.U.CUM.MIN;
            this.statisticData.U.AVG = response.data.data.U.CUM.AVG;
            this.statisticData.U.MAX = response.data.data.U.CUM.MAX;

            this.statisticData.I.MIN = response.data.data.I.CUM.MIN;
            this.statisticData.I.AVG = response.data.data.I.CUM.AVG;
            this.statisticData.I.MAX = response.data.data.I.CUM.MAX;

            this.statisticData.P.MIN = response.data.data.P.CUM.MIN;
            this.statisticData.P.AVG = response.data.data.P.CUM.AVG;
            this.statisticData.P.MAX = response.data.data.P.CUM.MAX;

            this.statisticData.loading = false;
          }
        })
        .catch(error => {
          if (error.response.data.status == "FAIL") {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getEnergyData() {
      
      let axiosConfig = {
        method: "POST",
        data: {
          data: "E_I",
          phase: this.phasesAPI,
          from: Number(this.statsDate.from),
          to: Number(this.statsDate.from) + 60,
          cumulative: true
        },
      };

      if (this.selectedDevice.macList) {
        axiosConfig.url = this.$ENDPOINTS.API_R + "/profile/group";
        axiosConfig.data.user_token = getUserToken();
        axiosConfig.data.macList = this.selectedDevice.macList;
      } else {
        axiosConfig.url = this.$ENDPOINTS.API_R + "/profile";
        axiosConfig.data.token = getSelectedDeviceToken(
          this.selectedDevice.mac
        );
      }

      axios(axiosConfig)
        .then(response => {
          this.statisticData.E_I.start = response.data.data[0].E_I.cumulative;
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.code == 610) {
              this.statisticData.E_I.start = 0;
              this.$refs.snackbar.notify(
                this.$lang.messages.energy_not_found_for_selected_time
              );
            } else {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
        });
    },

    getHierarchyData() {
      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/facility/devices-hierarchy",
        data: {
          token: getUserToken()
        },
        withCredentials: true
      })
        .then(response => {
          response.data.data.tags.forEach(element => {
            if (
              element.hasOwnProperty("lat") &&
              element.hasOwnProperty("lng")
            ) {
              const deviceListIndex = this.deviceList.findIndex(
                e => e.mac == element.mac
              );

              const mapMarker = {
                position: {
                  lat: Number(element.lat),
                  lng: Number(element.lng)
                },
                data: {
                  title: element.title,
                  tag: element.tags[0],
                  mac: element.mac,
                  status: this.deviceList[deviceListIndex].status
                }
              };

              this.locations.push(mapMarker);
            }
            if (element.tags.length > 0) {
              this.devicesStatsData.push(element.tags[0]);
            }
          });
          //this.initMap();
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.code != 610) {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
          //this.initMap();
        });
    },

    getEnergyChartData(apiFrom, apiTo, { chart }, period, type) {
      chart.showLoading();
      const dataType = type == 1 ? "E_I,E_E" : "Q1,Q2,Q3,Q4";

      let axiosConfig = {
        method: "POST",
        data: {
          data: dataType,
          phase: "CUM",
          from: apiFrom,
          to: apiTo,
          period: period,
          format: "H"
        },
        withCredentials: true
      };

      if (this.selectedDevice.macList) {
        axiosConfig.url = this.$ENDPOINTS.API_R + "/energy/group";
        axiosConfig.data.user_token = getUserToken();
        axiosConfig.data.macList = this.selectedDevice.macList;
      } else {
        axiosConfig.url = this.$ENDPOINTS.API_R + "/energy";
        axiosConfig.data.token = getSelectedDeviceToken(
          this.selectedDevice.mac
        );
      }

      axios(axiosConfig)
        .then(response => {
          if (response.data.status === "OK") {
            try {
              if (type == 1) {
                chart.series[0].setData(
                  response.data.data.E_I.CUM,
                  false,
                  false,
                  false
                );
                chart.series[1].setData(
                  response.data.data.E_E.CUM,
                  false,
                  false,
                  false
                );
              } else {
                chart.series[0].setData(
                  response.data.data.Q1.CUM,
                  false,
                  false,
                  false
                );
                chart.series[1].setData(
                  response.data.data.Q2.CUM,
                  false,
                  false,
                  false
                );
                chart.series[2].setData(
                  response.data.data.Q3.CUM,
                  false,
                  false,
                  false
                );
                chart.series[3].setData(
                  response.data.data.Q4.CUM,
                  false,
                  false,
                  false
                );
              }
              chart.xAxis[0].setExtremes(null, null, true);
            } catch (err) {
              return 0;
            }
          }
        })
        .catch(error => {
          if (error.response.data.code === 610) {
            this.$refs.snackbar.notify(this.$lang.messages.data_not_found);
            if (type == 1) {
              chart.series[0].setData([], false, false, false);
              chart.series[1].setData([]);
            } else {
              chart.series[0].setData([], false, false, false);
              chart.series[1].setData([], false, false, false);
              chart.series[2].setData([], false, false, false);
              chart.series[3].setData([]);
            }
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    initChartActiveEnergy() {
      const onyAxisSelect = type => {
        this.$store.commit("setyAxisType", type);
      };
      this.activeEnergyChartOptions = JSON.parse(JSON.stringify(chartOptions));
      this.activeEnergyChartOptions.title.text = "";
      this.activeEnergyChartOptions.yAxis.labels.format = "{value} kWh";
      this.activeEnergyChartOptions.tooltip.pointFormat =
        "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} kWh</b><br/>";
      this.activeEnergyChartOptions.series = [
        {
          name: this.$lang.charts.import,
          type: "column",
          marker: {
            enabled: false
          },
          dashStyle: "Solid",
          data: []
        },
        {
          name: this.$lang.charts.export,
          type: "column",
          visible: false,
          marker: {
            enabled: false
          },
          dashStyle: "Solid",
          data: []
        }
      ];
      this.activeEnergyChartOptions.yAxis.type = this.selectedyAxisType;
      this.activeEnergyChartOptions.chart.events = {
        redraw: function() {
          this.hideLoading();
        }
      };

      this.activeEnergyChartOptions.exporting.buttons.contextButton.menuItems.push(
        "separator"
      );
      this.activeEnergyChartOptions.exporting.buttons.contextButton.menuItems.push(
        {
          textKey: "scalerChange",
          onclick: function() {
            if (this.userOptions.yAxis.type == "logarithmic") {
              this.userOptions.yAxis.type = "linear";
              this.yAxis[0].update({ type: "linear" });
              onyAxisSelect("linear");
            } else {
              this.userOptions.yAxis.type = "logarithmic";
              this.yAxis[0].update({ type: "logarithmic" });
              onyAxisSelect("logarithmic");
            }
          }
        }
      );
    },

    initChartReactiveEnergy() {
      const onyAxisSelect = type => {
        this.$store.commit("setyAxisType", type);
      };
      this.reactiveEnergyChartOptions = JSON.parse(
        JSON.stringify(chartOptions)
      );
      this.reactiveEnergyChartOptions.title.text = "";
      this.reactiveEnergyChartOptions.yAxis.labels.format = "{value} kVArh";
      this.reactiveEnergyChartOptions.tooltip.pointFormat =
        "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} kVArh</b><br/>";
      this.reactiveEnergyChartOptions.series = [
        {
          name: this.$lang.charts.quadrant + " 1",
          type: "column",
          marker: {
            enabled: false
          },
          dashStyle: "Solid",
          data: []
        },
        {
          name: this.$lang.charts.quadrant + " 2",
          type: "column",
          marker: {
            enabled: false
          },
          visible: false,
          dashStyle: "Solid",
          data: []
        },
        {
          name: this.$lang.charts.quadrant + " 3",
          type: "column",
          marker: {
            enabled: false
          },
          visible: false,
          dashStyle: "Solid",
          data: []
        },

        {
          name: this.$lang.charts.quadrant + " 4",
          type: "column",
          marker: {
            enabled: false
          },
          dashStyle: "Solid",
          data: []
        }
      ];
      this.reactiveEnergyChartOptions.yAxis.type = this.selectedyAxisType;
      this.reactiveEnergyChartOptions.chart.events = {
        redraw: function(event) {
          this.hideLoading();
        }
      };

      this.reactiveEnergyChartOptions.exporting.buttons.contextButton.menuItems.push(
        "separator"
      );
      this.reactiveEnergyChartOptions.exporting.buttons.contextButton.menuItems.push(
        {
          textKey: "scalerChange",
          onclick: function() {
            if (this.userOptions.yAxis.type == "logarithmic") {
              this.userOptions.yAxis.type = "linear";
              this.yAxis[0].update({ type: "linear" });
              onyAxisSelect("linear");
            } else {
              this.userOptions.yAxis.type = "logarithmic";
              this.yAxis[0].update({ type: "logarithmic" });
              onyAxisSelect("logarithmic");
            }
          }
        }
      );
    },

    voltageFormat(value) {
      return `${Number(value).toFixed(1)} V`;
    },

    currentFormat(value) {
      if (value > 1000) {
        return `${Number(value / 1000).toFixed(2)} kA`;
      } else if (value > 100) {
        return `${Number(value).toFixed(1)} A`;
      } else {
        return `${Number(value).toFixed(2)} A`;
      }
    },

    activePowerFormat(value) {
      if (value > 1000) {
        return `${Number(value / 1000).toFixed(2)} kW`;
      } else {
        return `${Number(value).toFixed(1)} W`;
      }
    },

    energyImportFormat(value) {
      if (value > 100000) {
        return `${Number(value / 1000).toFixed(2)} kWh`;
      } else {
        return `${Number(value).toFixed(2)} Wh`;
      }
    },


    getQRString(){

      let axiosConfig = {
        method: "POST",
        data: {
          deviceMAC: this.selectedDevice.mac,
          token: getUserToken(),
          name: this.selectedDevice.title,
          encode: true
        },
        withCredentials: true,
        url: this.$ENDPOINTS.API_R + "/qrtoken"
      };
      
      axios(axiosConfig).then(response => {
        
          if (response.data.status === "OK") 
          {
            this.qrcode = response.data.data.token;
          }
        })
        .catch(error => {
         
          if (error.response.data.status == "FAIL")
          {
            this.$refs.snackbar.notify(this.$lang.messages.api_communication_error+" QR");
          }
        });

    }

    // async initMap() {
    //   try {
    //     const google = await gmapsInit();
    //     const geocoder = new google.maps.Geocoder();
    //     const map = new google.maps.Map(document.getElementById("map"), {
    //       gestureHandling: "cooperative"
    //     });

    //     function placeMarkerAndPanTo(latLng, map) {
    //       var marker = new google.maps.Marker({
    //         position: latLng,
    //         map: map
    //       });
    //       map.panTo(latLng);
    //     }

    //     geocoder.geocode({ address: `Slovakia` }, (results, status) => {
    //       if (status !== `OK` || !results[0]) {
    //         throw new Error(status);
    //       }

    //       map.setCenter(results[0].geometry.location);
    //       map.fitBounds(results[0].geometry.viewport);
    //     });

    //     /*        adding tag by click
    //      map.addListener("click", function(e) {
    //       placeMarkerAndPanTo(e.latLng, map);
    //     }); */

    //     const markerClickHandler = marker => {
    //       map.setZoom(18);
    //       map.setCenter(marker.getPosition());

    //       var contentString =
    //         '<div id="content">' +
    //         '<div id="siteNotice">' +
    //         "</div>" +
    //         '<h4 id="firstHeading" class="firstHeading">' +
    //         marker.data.title +
    //         "</h4>" +
    //         '<div id="bodyContent">' +
    //         this.$lang.general.device +
    //         ": " +
    //         marker.data.tag +
    //         "</br>" +
    //         "MAC: " +
    //         marker.data.mac +
    //         "</br>" +
    //         this.$lang.general.status +
    //         ": " +
    //         marker.data.status +
    //         "</br>" +
    //         "</div>" +
    //         "</div>";

    //       var infowindow = new google.maps.InfoWindow({
    //         content: contentString
    //       });

    //       infowindow.open(map, marker);
    //     };

    //     const markers = this.locations.map(location => {
    //       const marker = new google.maps.Marker({ ...location, map });
    //       marker.addListener(`click`, () => markerClickHandler(marker));

    //       return marker;
    //     });

    //     /*       new MarkerClusterer(map, markers, {
    //     imagePath: `https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m`,
    //   }); */
    //   } catch (error) {
    //     console.error(error);
    //   }
    // }
  }
};
</script>

<style scoped>
#map {
  margin: 0;
  max-height: 500px;
}

.normal-header {
  padding: 0 !important;
  min-height: 0px;
}

/* .md-button.md-simple i,
.md-button.md-simple:hover i {
  color: #ffffff !important;
} */

.md-toolbar {
  position: inherit;
}

.modal-header {
  opacity: 1;
}

.card-title {
  margin: 10px;
}

.modal-body {
  padding: 0px 0px 16px;
}

.modal-body p {
  margin: 5px;
}

.dashboardChart {
  height: 350px;
}
</style>
